import { t } from '@/i18n';
import Big from 'big.js';
import { FALLBACK_TEXT } from './constants';
import { BigCalculator } from './number-calculator';
import {
  type AmountUnit,
  AmountUnitLabelModifier,
  AmountUnitMultipliers,
} from './schemas';

const intlNumberFormat = (
  value: number | bigint,
  options?: Intl.NumberFormatOptions | undefined,
): string => {
  return new Intl.NumberFormat('ja-JP', options).format(
    //NOTE: Converting -0 to 0 by adding 0 to value
    typeof value === 'number' ? value + 0 : value,
  );
};

type NegativeSignOptions = {
  useNegativeSign?: boolean;
};
type FormatNumberOptions = Pick<
  Intl.NumberFormatOptions,
  'minimumFractionDigits' | 'maximumFractionDigits' | 'useGrouping'
> &
  NegativeSignOptions;

export const formatNumber = (
  value: number | bigint | null | undefined,
  options?: FormatNumberOptions,
) => {
  if (value === null || value === undefined) {
    return FALLBACK_TEXT;
  }

  const { isAggregateValueZero, fallbackValue } =
    handleZeroValueWithNegativeCount(value, options);
  if (isAggregateValueZero) {
    return fallbackValue;
  }

  const formattedValue = intlNumberFormat(value, {
    maximumFractionDigits: options?.maximumFractionDigits,
    minimumFractionDigits: options?.minimumFractionDigits,
    useGrouping: options?.useGrouping,
  });

  return options?.useNegativeSign
    ? formatNegativeValue(formattedValue)
    : formattedValue;
};

type Operation = 'round' | 'floor' | 'ceil';
export type FormatValueByUnitOptions = {
  operation?: Operation;
  unit?: AmountUnit;
  showUnitLabel?: boolean;
  enableSeparator?: boolean;
  negativeCount?: number | null;
  isDebugMode?: boolean;
} & NegativeSignOptions;

export const numberOperator = (
  value: number,
  operation?: FormatValueByUnitOptions['operation'],
): number => {
  if (!operation) {
    return value;
  }
  return operation === 'ceil' || operation === 'floor'
    ? Math[operation](value)
    : Big(value).round().toNumber();
};

export const handleZeroValueWithNegativeCount = (
  value: number | bigint | null | undefined,
  options?: FormatValueByUnitOptions,
):
  | {
      isAggregateValueZero: true;
      fallbackValue: string;
    }
  | {
      isAggregateValueZero: false;
      fallbackValue: number | null;
    } => {
  const hasNegativeValue = options?.negativeCount && options.negativeCount > 0;
  const isDebugMode = options?.isDebugMode;
  if (hasNegativeValue && value === 0) {
    return {
      isAggregateValueZero: true,
      fallbackValue: isDebugMode
        ? t('dev-tools.aggregate-value-is-zero')
        : FALLBACK_TEXT,
    };
  }
  return {
    isAggregateValueZero: false,
    fallbackValue:
      typeof value === 'number' || typeof value === 'bigint'
        ? Number(value)
        : null,
  };
};

export const formatValueByUnit = (
  value: number | null | undefined,
  options?: FormatValueByUnitOptions,
): string => {
  if (typeof value !== 'number') {
    return FALLBACK_TEXT;
  }

  const displayValue: Record<AmountUnit, number> = {
    plain: value,
    thousand: numberOperator(
      formatAmountNumberByUnit(value, 'thousand'),
      options?.operation,
    ),
    million: numberOperator(
      formatAmountNumberByUnit(value, 'million'),
      options?.operation,
    ),
  };
  const unit = options?.unit ?? 'plain';
  const valueByUnit = displayValue[unit];

  /* 負の値の数が存在し、単位変換後の値が0だが、元の値が0でない場合は'0'を返す
    例: 1000円を千円単位で表示する場合、100 -> 0.1 -> 0（四捨五入）となり、
    元の値が0でないのに表示が--になってしまうため、特別な処理が必要
  */
  if (
    typeof options?.negativeCount === 'number' &&
    valueByUnit === 0 &&
    displayValue.plain !== 0
  ) {
    return '0';
  }

  const formattedValue = formatNumber(valueByUnit, {
    useNegativeSign: true,
    useGrouping: options?.enableSeparator,
    ...options,
  });
  return options?.showUnitLabel
    ? `${formattedValue} ${AmountUnitLabelModifier[unit]}`
    : formattedValue;
};

export const formatNegativeValue = (value: string): string => {
  return value.replace('-', '△');
};

export const formatAmountNumberByUnit = (
  value: number,
  unit: AmountUnit,
): number => {
  return BigCalculator.divide(value, AmountUnitMultipliers[unit]) ?? 0;
};

type FormatPercentageOptions = FormatNumberOptions & {
  hideUnit?: boolean;
};

export const formatPercentage = (
  value: number | null | undefined,
  options?: FormatPercentageOptions,
) => {
  if (typeof value !== 'number') {
    return FALLBACK_TEXT;
  }

  let percentage = '';

  percentage = intlNumberFormat(value, {
    style: 'percent',
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
    minimumFractionDigits: options?.minimumFractionDigits,
  });

  if (options?.hideUnit) {
    percentage = percentage.replace('%', '');
  }

  if (options?.useNegativeSign) {
    percentage = formatNegativeValue(percentage);
  }

  return percentage;
};

export const formatFraction = (
  numerator: number | null | undefined,
  denominator: number | null | undefined,
  options?: FormatPercentageOptions,
) => {
  if (typeof numerator !== 'number' || typeof denominator !== 'number') {
    return FALLBACK_TEXT;
  }

  const result = BigCalculator.divide(numerator, denominator);

  return formatPercentage(result, options);
};

/**
 * 数値を負数に変換する関数。主に合計額の計算のために使用する。
 * - 入力が数値でない場合は 0 を返す
 * - 0 の場合は 0 を返す
 * - 正数の場合は -1 を掛けて負数に変換
 * - 負数の場合は正の値を返す
 */
export const convertToNegative = (value: number | null | undefined): number => {
  if (!value) {
    return 0;
  }
  return value * -1;
};

/**
 * 金額を指定された単位に変換する関数
 * @param value 変換する金額
 * @param unit 変換後の単位（'plain' | 'thousand' | 'million'）
 * @param operation 端数処理の方法（'round' | 'floor' | 'ceil'）
 * @returns 変換後の金額
 */
export const convertAmountByUnit = ({
  value,
  unit = 'plain',
  operation,
}: {
  value: number | null | undefined;
  unit?: AmountUnit;
  operation?: Operation;
}): number => {
  if (typeof value !== 'number') {
    return 0;
  }

  const convertedValue = formatAmountNumberByUnit(value, unit);
  return numberOperator(convertedValue, operation);
};
